import { amplitudeLib } from 'freedom-shared';

import type { QuickBookingVariant } from '@/features/quick-booking/model/use-quick-booking-experiment';

export type QuickBookingFlow = 'quick_booking' | 'classic';

export type QuickBookingFallbackReason =
  | 'offer_expired'
  | 'double_booking'
  | 'init_error_5xx'
  | 'init_error_4xx'
  | 'validation_failed'
  | 'funsun_leaked'
  | 'submit_failed';

interface PaxBreakdown {
  adt: number;
  chd: number;
  inf: number;
  yth: number;
}

interface AssignedParams {
  variant: QuickBookingVariant;
  offerId: string;
  providerClass: string;
  tripType: 'OW' | 'RT';
  paxCount: number;
  paxBreakdown: PaxBreakdown;
  isDomestic: boolean;
}

interface PageOpenParams {
  offerId: string;
  searchQuery: string;
  providerClass: string;
  hasWarnings: boolean;
  tripType: 'OW' | 'RT';
  paxCount: number;
  paxBreakdown: PaxBreakdown;
  isDomestic: boolean;
  preselectedPackageId: string | null;
}

interface PassengerSavedParams {
  offerId: string;
}

interface SeatSelectedParams {
  offerId: string;
  seatCount: number;
  seatTotalPrice: number;
}

interface PayClickParams {
  offerId: string;
  totalPrice: string;
  ancillaryBaggageCount: number;
  hasSeats: boolean;
  hasInsurance: boolean;
}

interface FallbackParams {
  offerId: string;
  reason: QuickBookingFallbackReason;
  httpStatus?: number;
  errorMessage?: string;
}

export const quickBookingAssigned = (params: AssignedParams) =>
  amplitudeLib.logEvent('avfree_quick_booking_assigned', {
    variant: params.variant,
    offer_id: params.offerId,
    search_mode: params.tripType,
    passenger_count: params.paxCount,
    passenger_adt: params.paxBreakdown.adt,
    passenger_chd: params.paxBreakdown.chd,
    passenger_inf: params.paxBreakdown.inf,
    passenger_yth: params.paxBreakdown.yth,
    is_domestic: params.isDomestic,
    provider_class: params.providerClass,
  });

export const quickBookingPageOpen = (params: PageOpenParams) =>
  amplitudeLib.logEvent('avfree_quick_booking_page_open', {
    offer_id: params.offerId,
    search_query: params.searchQuery,
    provider_class: params.providerClass,
    has_warnings: params.hasWarnings,
    search_mode: params.tripType,
    passenger_count: params.paxCount,
    passenger_adt: params.paxBreakdown.adt,
    passenger_chd: params.paxBreakdown.chd,
    passenger_inf: params.paxBreakdown.inf,
    passenger_yth: params.paxBreakdown.yth,
    is_domestic: params.isDomestic,
    preselected_package_id: params.preselectedPackageId,
  });

export const quickBookingPassengerSaved = (params: PassengerSavedParams) =>
  amplitudeLib.logEvent('avfree_quick_booking_passenger_saved', {
    offer_id: params.offerId,
  });

export const quickBookingSeatSelected = (params: SeatSelectedParams) =>
  amplitudeLib.logEvent('avfree_quick_booking_seat_selected', {
    offer_id: params.offerId,
    seat_count: params.seatCount,
    seat_total_price: params.seatTotalPrice,
  });

export const quickBookingPayClick = (params: PayClickParams) =>
  amplitudeLib.logEvent('avfree_quick_booking_pay_click', {
    offer_id: params.offerId,
    total_price: params.totalPrice,
    ancillary_baggage_count: params.ancillaryBaggageCount,
    has_seats: params.hasSeats,
    has_insurance: params.hasInsurance,
  });

export const quickBookingFallback = (params: FallbackParams) =>
  amplitudeLib.logEvent('avfree_quick_booking_fallback', {
    offer_id: params.offerId,
    reason: params.reason,
    http_status: params.httpStatus,
    error_message: params.errorMessage,
  });
